import { useEffect, useRef, useState } from "react";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { hapticImpactSync } from "@/lib/native/capacitorBridge";

const THRESHOLD_PX = 72;
const MAX_PULL_PX = 96;

type Options = {
  enabled: boolean;
  onRefresh: () => Promise<void>;
};

function isInsideOpenOverlay(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest('[role="dialog"][data-state="open"]') ||
      target.closest('[data-radix-dialog-overlay][data-state="open"]'),
  );
}

export function usePullToRefresh({ enabled, onRefresh }: Options) {
  const { isNative } = useNativePlatform();
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const pulling = useRef(false);
  const pullDistanceRef = useRef(0);
  const refreshingRef = useRef(false);
  const hapticFiredRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    refreshingRef.current = refreshing;
  }, [refreshing]);

  useEffect(() => {
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!enabled || !isNative) return;

    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current || window.scrollY > 0) return;
      if (e.touches.length !== 1) return;
      if (isInsideOpenOverlay(e.target)) return;
      startY.current = e.touches[0].clientY;
      pulling.current = true;
      hapticFiredRef.current = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pulling.current || refreshingRef.current) return;
      if (isInsideOpenOverlay(e.target)) {
        pulling.current = false;
        pullDistanceRef.current = 0;
        setPullDistance(0);
        return;
      }
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) {
        pullDistanceRef.current = 0;
        setPullDistance(0);
        return;
      }
      if (window.scrollY > 0) {
        pulling.current = false;
        pullDistanceRef.current = 0;
        setPullDistance(0);
        return;
      }
      e.preventDefault();
      const dist = Math.min(dy * 0.45, MAX_PULL_PX);
      pullDistanceRef.current = dist;
      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          setPullDistance(pullDistanceRef.current);
        });
      }
      if (dist >= THRESHOLD_PX && !hapticFiredRef.current) {
        hapticFiredRef.current = true;
        hapticImpactSync("light");
      }
    };

    const onTouchEnd = async () => {
      if (!pulling.current) return;
      pulling.current = false;
      const shouldRefresh = pullDistanceRef.current >= THRESHOLD_PX;
      pullDistanceRef.current = 0;
      setPullDistance(0);
      hapticFiredRef.current = false;
      if (!shouldRefresh) return;
      setRefreshing(true);
      refreshingRef.current = true;
      hapticImpactSync("medium");
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
        refreshingRef.current = false;
      }
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd);
    document.addEventListener("touchcancel", onTouchEnd);

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [enabled, onRefresh, isNative]);

  return { pullDistance, refreshing };
}
